import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  AreaChart,
  Area,
} from "recharts";
import { api, formatApiError } from "@/api";
import { AppShell } from "@/components/AppShell";

const RANGES = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
];

const SENSORS = [
  { id: "food_fridge", title: "Walk-in Fridge", accent: "#10b981" },
  { id: "food_freezer", title: "Walk-in Freezer", accent: "#60a5fa" },
];

function formatTick(iso, range) {
  const d = new Date(iso);
  if (range === "24h") {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ThresholdLines({ thresholds }) {
  if (!thresholds) return null;
  const lines = [];
  const items = [
    { f: "warn_high_f", label: "warn hi", color: "#f59e0b" },
    { f: "warn_low_f", label: "warn lo", color: "#f59e0b" },
    { f: "critical_high_f", label: "crit hi", color: "#ef4444" },
    { f: "critical_low_f", label: "crit lo", color: "#ef4444" },
  ];
  for (const it of items) {
    const v = thresholds[it.f];
    if (v == null) continue;
    lines.push(
      <ReferenceLine
        key={it.f}
        y={v}
        stroke={it.color}
        strokeDasharray="4 4"
        strokeOpacity={0.7}
        label={{
          value: `${it.label} ${v}°F`,
          position: "right",
          fill: it.color,
          fontSize: 10,
          fontWeight: 600,
        }}
      />,
    );
  }
  return lines;
}

function SensorChart({ sensor, thresholds }) {
  const [range, setRange] = useState("24h");
  const [points, setPoints] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/sensors/${sensor.id}/readings?range=${range}`,
      );
      setPoints(data?.points || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [sensor.id, range]);

  useEffect(() => {
    load();
  }, [load]);

  const isRaw = range === "24h";
  const yMin = Math.floor(
    Math.min(
      ...points.map((p) => (isRaw ? p.temp_f : p.min_f)),
      thresholds?.warn_low_f ?? Infinity,
      thresholds?.critical_low_f ?? Infinity,
    ),
  );
  const yMax = Math.ceil(
    Math.max(
      ...points.map((p) => (isRaw ? p.temp_f : p.max_f)),
      thresholds?.warn_high_f ?? -Infinity,
      thresholds?.critical_high_f ?? -Infinity,
    ),
  );
  const yDomain = Number.isFinite(yMin) && Number.isFinite(yMax)
    ? [yMin - 1, yMax + 1]
    : ["auto", "auto"];

  return (
    <div
      className="border border-zinc-800 bg-zinc-900/40 p-6"
      data-testid={`history-chart-${sensor.id}`}
    >
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {sensor.id}
          </div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
            {sensor.title}
          </h2>
        </div>
        <div
          className="flex gap-1 border border-zinc-800"
          data-testid={`history-range-${sensor.id}`}
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`history-range-${sensor.id}-${r.key}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${
                range === r.key
                  ? "bg-zinc-100 text-zinc-950"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400 border-l-2 border-red-500 pl-3 mb-3">
          {error}
        </div>
      )}

      <div className="w-full min-h-[280px]" style={{ height: 280 }}>
        {points.length === 0 && !loading ? (
          <div
            className="h-full flex items-center justify-center text-zinc-500 text-sm"
            data-testid={`history-empty-${sensor.id}`}
          >
            No readings in this window yet.
          </div>
        ) : isRaw ? (
          <ResponsiveContainer width="100%" height={280} minWidth={0}>
            <LineChart data={points} margin={{ top: 8, right: 70, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#3f3f46" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="t"
                tickFormatter={(t) => formatTick(t, range)}
                stroke="#71717a"
                fontSize={11}
                tickMargin={6}
              />
              <YAxis
                domain={yDomain}
                stroke="#71717a"
                fontSize={11}
                tickFormatter={(v) => `${v}°F`}
                width={50}
              />
              <Tooltip
                contentStyle={{
                  background: "#09090b",
                  border: "1px solid #27272a",
                  fontSize: 12,
                }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [`${Number(v).toFixed(1)}°F`, "Temp"]}
              />
              <ThresholdLines thresholds={thresholds} />
              <Line
                type="monotone"
                dataKey="temp_f"
                stroke={sensor.accent}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={280} minWidth={0}>
            <AreaChart data={points} margin={{ top: 8, right: 70, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#3f3f46" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="t"
                tickFormatter={(t) => formatTick(t, range)}
                stroke="#71717a"
                fontSize={11}
                tickMargin={6}
              />
              <YAxis
                domain={yDomain}
                stroke="#71717a"
                fontSize={11}
                tickFormatter={(v) => `${v}°F`}
                width={50}
              />
              <Tooltip
                contentStyle={{
                  background: "#09090b",
                  border: "1px solid #27272a",
                  fontSize: 12,
                }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v, name) => [`${Number(v).toFixed(1)}°F`, name]}
              />
              <ThresholdLines thresholds={thresholds} />
              {/* Band between min/max (invisible bottom + visible top to simulate band) */}
              <Area
                type="monotone"
                dataKey="max_f"
                stroke="none"
                fill={sensor.accent}
                fillOpacity={0.18}
                isAnimationActive={false}
                name="max"
              />
              <Area
                type="monotone"
                dataKey="min_f"
                stroke="none"
                fill="#09090b"
                fillOpacity={1}
                isAnimationActive={false}
                name="min"
              />
              <Line
                type="monotone"
                dataKey="avg_f"
                stroke={sensor.accent}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                name="avg"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">
        {isRaw
          ? `${points.length} raw points`
          : `${points.length} buckets (min · avg · max)`}
      </div>
    </div>
  );
}

export default function History() {
  const [thresholds, setThresholds] = useState({});
  const [params] = useSearchParams();
  const focused = params.get("sensor");

  useEffect(() => {
    api
      .get("/thresholds")
      .then(({ data }) => {
        const map = {};
        data.forEach((t) => (map[t.sensor_id] = t));
        setThresholds(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (focused) {
      const el = document.querySelector(`[data-testid="history-chart-${focused}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focused]);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          Trends
        </div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">
          History
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          Threshold lines update as you change them on the Thresholds page. 7 d
          and 30 d views show the daily min / max band with the average line in
          the centre — so a freezer drift is obvious even when zoomed out.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {SENSORS.map((s) => (
          <SensorChart key={s.id} sensor={s} thresholds={thresholds[s.id]} />
        ))}
      </div>
    </AppShell>
  );
}
